import { sqHierarchiesApi } from '@/sdk/api/HierarchiesApi';
import { setHierarchyOutput } from '@/tableDefinitionEditor/assetBuilder.actions';
import { getTableDefinitionStore } from '@/tableDefinitionEditor/store.utilities';
import { setTableDefinition } from '@/tableDefinitionEditor/tableDefinition.actions';
import { CONTROL_PANE_DISPLAY } from '@/tableDefinitionEditor/tableDefinition.constants';
import {
  addOrUpdateColumnDefinitions,
  columnDefinitionOutputToColumnDefinitionInput,
  updateSortsInTheGrid,
  updateVisibilityInTheGrid,
} from '@/tableDefinitionEditor/tableDefinition.utilities';
import { setControlPane, setEnableTreeCreation } from '@/tableDefinitionEditor/tableDefinitionEditor.actions';
import { doTrack } from '@/track/trackProxy.service';
import type { ColumnState } from 'ag-grid-community';
import type { HierarchyInputV1 } from '@/sdk/model/HierarchyInputV1';
import type { HierarchyOutputV1 } from '@/sdk/model/HierarchyOutputV1';
import type { ViewColumnInputV1 } from '@/sdk/model/ViewColumnInputV1';
import type { ColumnDefinition } from '@/tableDefinitionEditor/tableDefinition.types';

export const hierarchyOutputToHierarchyInput = (hierarchyOutput: HierarchyOutputV1): HierarchyInputV1 => {
  return {
    name: hierarchyOutput.name,
    description: hierarchyOutput.description,
    scopedTo: hierarchyOutput.scopedTo,
    tableDefinitionId: hierarchyOutput.tableDefinition.id,
    aliasColumnId: hierarchyOutput.aliasColumnId,
    filterGrouping: hierarchyOutput.filterGrouping,
    viewColumns: hierarchyOutput.viewColumns.map(
      (col) =>
        ({
          ...col,
          viewColumnFilters: col.viewColumnFilters?.map((filter) => ({
            ...filter,
          })),
        }) as Required<ViewColumnInputV1>,
    ),
  };
};

export const applyViewColumnMetadataToTableDefinition = async (hierarchy: HierarchyOutputV1) => {
  const sqItemTableDefinitionStore = getTableDefinitionStore('ITEM');
  const viewColumns = hierarchy.viewColumns;
  const columns = sqItemTableDefinitionStore.columns;
  const agGridApi = await sqItemTableDefinitionStore.waitForAgGridApi();
  const columnsToShow = columns.filter((col) => viewColumns.some((viewCol) => viewCol.columnDefinition === col.id));
  const columnsWithSortAndHiddenApplied = columnsToShow.map((col) => {
    const viewColumn = viewColumns.find((viewCol) => viewCol.columnDefinition === col.id);
    return {
      ...col,
      sortAscending: viewColumn?.sortAscending,
      sortIndex: viewColumn?.sortIndex,
      isHidden: false,
    } as ColumnDefinition;
  });
  // For columns not a part of the hierarchy, hide them and remove their sorts. Add the sorts to included columns
  // and make sure they are not hidden.
  const columnsToHide = columns.filter((col) => !columnsToShow.some((viewCol) => viewCol.id === col.id));
  const columnsWithSortsRemoved = columnsToHide.map((col) => {
    return {
      ...col,
      sortIndex: undefined,
      isHidden: true,
    } as ColumnDefinition;
  });
  const allColumns = [...columnsWithSortAndHiddenApplied, ...columnsWithSortsRemoved];
  const allColumnAsInputs = allColumns.map((col) => {
    return columnDefinitionOutputToColumnDefinitionInput(col, columns);
  });

  const results = await addOrUpdateColumnDefinitions(allColumnAsInputs, sqItemTableDefinitionStore.id, true);
  if (!results) {
    console.error('Failed to update column definitions');
    return;
  }
  setTableDefinition('ITEM', results.tableDefinition);

  // Now apply group information, which is not stored in the backend, and update the visibility and sort state in
  // the grid
  if (agGridApi) {
    updateVisibilityInTheGrid(results.tableDefinition, agGridApi);
    updateSortsInTheGrid(results.tableDefinition, agGridApi);
    const columnsWithGroupOrder = columnsToShow
      .filter((col) =>
        viewColumns.some((viewCol) => viewCol.columnDefinition === col.id && viewCol.groupOrder !== undefined),
      )
      .map((col) => {
        const viewColumn = viewColumns.find((viewCol) => viewCol.columnDefinition === col.id);
        return {
          ...col,
          groupOrder: viewColumn?.groupOrder,
        };
      });
    const columnStates: ColumnState[] = columnsWithGroupOrder.map((col) => {
      return {
        colId: col.id,
        rowGroupIndex: col.groupOrder,
      } as ColumnState;
    });

    agGridApi.applyColumnState({ state: columnStates });
  }
};

export const loadHierarchyAndEdit = async (hierarchyId: string): Promise<void> => {
  // fetch the hierarchy fresh to ensure we have the latest version
  const { data: hierarchy } = await sqHierarchiesApi.getHierarchy({ id: hierarchyId });
  const itemTableId = hierarchy.tableDefinition.id;
  doTrack('Asset Builder', 'Edit Hierarchy', {
    itemTableId,
    hierarchyId,
  });
  setControlPane('ITEM', CONTROL_PANE_DISPLAY.HIERARCHY_CONTROL);
  setHierarchyOutput(hierarchy);
  setEnableTreeCreation('ITEM', true);
  await applyViewColumnMetadataToTableDefinition(hierarchy);
};

export const filterHierarchiesUsingThisColumnAsGroup = (columnId: string, hierarchies: HierarchyOutputV1[]) => {
  return hierarchies.filter((hierarchy) => {
    return hierarchy.viewColumns.some((viewColumn) => {
      return viewColumn.groupOrder !== undefined && viewColumn.columnDefinition === columnId;
    });
  });
};
